import MuiTableRow from '@material-ui/core/TableRow';
import FileDownloadIcon from '@material-ui/icons/CloudDownload';
import { styled } from '@material-ui/styles';
import { AppRoutes } from 'Configs/AppRoutes';
import { GestureEvent } from 'Events/withEvent';
import { EstimationUrlParameters, UrlHelper } from 'Helpers';
import { LocationDescriptor } from 'history';
import { Message } from 'Locales/I18nService';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { QuotationsApi } from 'Services/Api/QuotationsApi';
import { useVersion } from 'Types/RouteParams';
import { ExportUtils } from 'Utils/ExportUtils';
import { HStack } from 'Web/Components/Widgets/Stack';
import ControledTooltip from 'Web/Components/Widgets/TitleTooltip';
import { NunitoSansTitle, NunitoSansBoldTitle } from 'Web/Components/Widgets/Typographies';
import { useIsModeDebug } from 'Web/Context/Debug/DebugContext';

import { QuotationSummaryDto } from '../../../../Models/Projects/ProjectDto';
import { DateUtils } from '../../../../Utils/DateUtils';
import { Assets } from '../../../assets/Assets';
import { PriceLabel } from '../../../Components/Views/PriceLabel/PriceLabel';
import { MOBILE_LIMIT, useIsMobile } from '../../../Context/PlatformContext/PlatformContext';
import { ThemeRes } from '../../../Themes/ThemeRes';

export type QuotationListProps = { items?: QuotationSummaryDto[] };
export const QuotationList = ({ items = [] }: QuotationListProps) => {
    return (
        <Table>
            <HeaderContainer>
                <tr>
                    <HeaderCell>
                        <FormattedMessage id="ProjectDetails_Version_CreationDate" />
                    </HeaderCell>
                    <HeaderCell>
                        <HeaderRoomCount />
                    </HeaderCell>
                    <HeaderCell>
                        <FormattedMessage id="ProjectDetails_version_Amount" />
                    </HeaderCell>
                </tr>
            </HeaderContainer>
            <tbody>
                {items.map((item) => (
                    <QuotationItemRow key={item.id} {...item} />
                ))}
            </tbody>
        </Table>
    );
};

const HeaderRoomCount = () => {
    return (
        <FormattedMessage
            id={useIsMobile() ? 'ProjectDetails_Version_RoomCount_Short' : 'ProjectDetails_Version_RoomCount'}
        />
    );
};

const HeaderCell = styled('th')({
    fontFamily: ThemeRes.Fonts.fontFamilyNunitoSans,
    fontSize: 16,
    color: 'black',
    padding: '12px 0px 12px 16px',
    whiteSpace: 'nowrap',
    fontWeight: 400,
    '&:first-child': { width: '70%' },
    '&:nth-child(2)': { width: '25%' },
    '&:last-child': { padding: 12 },
});
const HeaderContainer = styled('thead')({
    textAlign: 'left',
    borderBottom: '1px solid rgba(19, 19, 58, 0.15)',
    borderTop: '1px solid rgba(19, 19, 58, 0.15)',
});
const Table = styled('table')({
    maxWidth: '75%',
    borderCollapse: 'collapse',
    borderSpacing: '0 8px',
    marginLeft: 'auto',
    marginRight: 'auto',
    [`@media (max-width: ${MOBILE_LIMIT.WIDTH_900}px)`]: { maxWidth: '100%' },
});

export const QuotationItemRow = (item: QuotationSummaryDto) => {
    const version = useVersion();
    const to: LocationDescriptor<unknown> = {
        pathname: AppRoutes.ESTIMATION,
        search: UrlHelper.toQueryParams<EstimationUrlParameters>({
            version,
            configId: item.configuration_id,
        }).toString(),
    };
    return (
        <MuiTableRow component={(props: any) => <StyledLink {...props} to={to} />}>
            <Cell>
                <DateContainer>
                    <QuotationFile id={item.id} crmKey={item.crm_key}>
                        <Assets.IconDevis />
                    </QuotationFile>
                    {DateUtils.formatDateTime(item.created_date, <FormattedMessage id="COMMON_DATE_DEFAULT_VALUE" />)}
                </DateContainer>
            </Cell>
            <Cell>
                <RoomCountContainer>
                    <RoomCount>{item.room_count}</RoomCount>
                    <RoomLabel>
                        <FormattedMessage id="ProjectDetails_RoomCountValue" />
                    </RoomLabel>
                </RoomCountContainer>
            </Cell>
            <Cell>
                <PriceContainer>
                    <PriceLabel price={item.total_amount} variant="noComma" />
                </PriceContainer>
                {!item.with_prestation && <WithoutPrestation />}
            </Cell>
            <Cell>
                <Assets.IconArrowRightThin style={{ width: 7, height: 14 }} />
            </Cell>
        </MuiTableRow>
    );
};
const StyledLink = styled(Link)({
    textDecoration: 'none !important',
    background: 'white',
    color: 'black',
    fontFamily: ThemeRes.Fonts.fontFamilyNunitoSansBold,
    fontSize: 14,
    height: 83,
    alignItems: 'center',
    border: '0.5px solid #ffffff',
    borderStyle: 'inset',
    '@media (hover)': {
        '&:hover': { border: '1px solid #13123A' },
    },
});

const DateContainer = styled('div')({ display: 'flex', gap: 32, alignItems: 'center' });
const PriceContainer = styled(NunitoSansBoldTitle)({ fontSize: 18 });
const RoomCountContainer = styled('div')({ display: 'flex', gap: 5, alignItems: 'center' });
const RoomCount = styled('span')({ fontSize: 18 });
const RoomLabel = styled('span')({
    fontFamily: ThemeRes.Fonts.fontFamilyNunitoSans,
    fontSize: 16,
    fontWeight: 400,
});

const Cell = styled('td')({
    height: '71px',
    paddingLeft: 15,
    whiteSpace: 'nowrap',
    '&:first-child': { whiteSpace: 'normal' },
    '&:last-child': {
        textAlign: 'right',
        paddingLeft: 32,
        paddingRight: 24,
        [`@media (max-width: ${MOBILE_LIMIT.WIDTH_900}px)`]: { paddingLeft: 39, paddingRight: 17 },
    },
});

const WithoutPrestationTitle = styled(NunitoSansTitle)({
    martinTop: 8,
    fontWeight: 800,
    fontSize: 14,
    lineHeight: '10.34px',
    color: '#13123A',
    border: '1px solid #E5A033',
    borderRadius: 34,
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
});
const WithoutPrestation = () => {
    return (
        <WithoutPrestationTitle>
            <IconWithoutPrestation />
            <Message id="ProjectDetails_WithoutPrestation" />
        </WithoutPrestationTitle>
    );
};

const IconWithoutPrestation = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M18.49 8.188c.313.288.506.68.506 1.11 0 2.425-.001 2.892-.002 2.993h.001v2.382c0 1.68-.701 3.266-1.975 4.464C15.744 20.338 14.05 21 12.253 21c-1.783 0-3.463-.653-4.737-1.838l.777-.777.018.016a5.73 5.73 0 0 0 3.942 1.544 5.73 5.73 0 0 0 3.942-1.544c1.063-1 1.649-2.324 1.649-3.728V9.299a.5.5 0 0 0-.134-.331zM6.764 15.67c.11.538.308 1.054.589 1.532l-.816.816a6 6 0 0 1-.704-1.417zM12.24 3c.845 0 1.557.518 1.755 1.215.22-.085.464-.132.719-.132.436 0 .837.138 1.15.367l-.783.782a.74.74 0 0 0-.367-.094c-.363 0-.658.248-.658.552v.569L12.9 7.412V4.608c0-.305-.296-.553-.659-.553s-.658.248-.658.552v4.125L10.426 9.89V5.33c0-.305-.296-.553-.658-.553s-.658.248-.659.553v5.678c0 .29-.257.527-.575.527s-.577-.236-.577-.527V7.472c0-.305-.294-.553-.655-.553s-.655.248-.655.553l.013 6.181L5.515 14.8l-.003-.126-.016-7.2c0-.888.81-1.609 1.806-1.609.231 0 .453.039.656.11V5.33c0-.886.812-1.607 1.81-1.607.304 0 .591.068.843.186.294-.537.914-.908 1.63-.908m4.288 9.413c0 .291-.257.527-.575.527h-.006c-.317 0-.575-.236-.575-.527v-1.106l1.156-1.157zm-2.472-2.46c0 .29-.258.526-.575.527h-.002c-.317 0-.574-.237-.575-.528v-.42l1.152-1.152zm2.467-2.15q.15-.052.311-.081l-1.462 1.463V7.063l1.151-1.15z"
            fill="#13123A"
        />
        <path
            d="m19.968 2.47.117-.097a.751.751 0 0 1 1.04 1.04l-.096.117-16.5 16.5a.75.75 0 0 1-1.061-1.06z"
            fill="#E5A033"
        />
    </svg>
);

type QuotationFileProps = { id: string; crmKey?: string };
const QuotationFile = ({ children, ...file }: React.PropsWithChildren<QuotationFileProps>) => {
    const isDebugMode = useIsModeDebug();
    return (
        <ControledTooltip
            placement="bottom"
            title={<DonwloadQuotationFile {...file} />}
            disableHoverListener={!isDebugMode || !file.crmKey}
            leaveDelay={0}
            enterDelay={50}>
            <span>{children}</span>
        </ControledTooltip>
    );
};

const DonwloadQuotationFile = (file: QuotationFileProps) => {
    const download = async (e: GestureEvent) => {
        e?.stopPropagation();
        if (file.crmKey) {
            const data = await QuotationsApi.getQuotationFile(file.crmKey);
            ExportUtils.saveToJSon(data, `${file.crmKey}.json`);
        } else {
        }
    };
    return (
        <HStack onClick={download} style={{ cursor: 'pointer', gap: 4 }}>
            <FileDownloadIcon /> Télécharger
        </HStack>
    );
};
